/**
 * THE DATE PICKERS' MONTH AND DAY NAMES IN THE APP'S LANGUAGE (design 29, W5).
 *
 * Mantine's pickers name months and weekdays through dayjs, which knows only English until a
 * locale file is imported. Each is a small chunk, fetched the first time its language is chosen, so
 * the apps do not carry thirteen of them. Listed by hand: a bundler cannot split an import whose
 * package path is computed.
 */
import { useEffect, useState } from 'react';

type LocaleModule = Promise<{ readonly default: { readonly name: string } }>;

/** The platform's languages other than English, to dayjs's locale for each. */
const DAYJS_LOCALES: Readonly<Record<string, () => LocaleModule>> = {
  ru: () => import('dayjs/locale/ru'),
  pl: () => import('dayjs/locale/pl'),
  es: () => import('dayjs/locale/es'),
  de: () => import('dayjs/locale/de'),
  fr: () => import('dayjs/locale/fr'),
  it: () => import('dayjs/locale/it'),
  // `Intl` reads a bare `pt` as Brazilian, so the pickers do too.
  pt: () => import('dayjs/locale/pt-br'),
  ja: () => import('dayjs/locale/ja'),
  zh: () => import('dayjs/locale/zh-cn'),
  uk: () => import('dayjs/locale/uk'),
  nl: () => import('dayjs/locale/nl'),
  ko: () => import('dayjs/locale/ko'),
  tr: () => import('dayjs/locale/tr'),
};

/** Loads `language`'s dayjs locale and answers its name; English, or a language not listed, is 'en'. */
export async function loadDatesLocale(language: string): Promise<string> {
  const load = DAYJS_LOCALES[language];
  return load === undefined ? 'en' : (await load()).default.name;
}

/**
 * The dayjs locale for `language` once it is loaded. Until then - and if its chunk never arrives -
 * the locale already shown stays, so a picker is never left without names.
 */
export function useDatesLocale(language: string | undefined): string {
  const [locale, setLocale] = useState('en');
  useEffect(() => {
    if (language === undefined) {
      return undefined;
    }
    let live = true;
    loadDatesLocale(language).then(
      (loaded) => {
        if (live) {
          setLocale(loaded);
        }
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [language]);
  return locale;
}
